import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faListCheck,
  faComments,
  faPeopleLine,
  faFlaskVial,
  faHandshake,
  faChess,
  faPeopleArrows,
  faHourglassHalf,
  faCalendar,
  faFileArrowDown,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import {
  companySizes,
  companySizeValue,
  courseValueMapping,
  errorOnSubscriptionMessages,
} from '../constants/NewsletterSubscriptions';
import { isValidEmail } from '../utils/utils';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { MailerAPI } from '../utils/mailer-api';

const mailerAPI = new MailerAPI();

const translations = {
  es: {
    titleBefore: 'Y donde tú no llegas, ',
    titleUnderline: 'llegamos nosotros',
    subtitle: 'Descubre nuestros servicios para desarrollar a tu equipo',
    categories: [
      {
        title: 'Herramientas de Productividad',
        color: '#FF2300',
        icon: faListCheck,
        estimatedTime: '4h',
        estimatedDays: 2,
      },
      {
        title: 'Herramientas de Comunicación',
        color: '#FF4500',
        icon: faComments,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Herramientas de Equipo',
        color: '#FF8C00',
        icon: faPeopleLine,
        estimatedTime: '4h',
        estimatedDays: 2,
      },
      {
        title: 'Herramientas de Innovación',
        color: '#FFA500',
        icon: faFlaskVial,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Herramientas de Reunión',
        color: '#FFB300',
        icon: faHandshake,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Herramientas de Estrategia',
        color: '#FFC107',
        icon: faChess,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Sesiones Personalizadas',
        color: '#FF6F00',
        icon: faPeopleArrows,
        estimatedTime: '1h',
        estimatedDays: 1,
      },
    ],
    contactForm: {
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Email',
      phone: 'Teléfono',
      companyName: 'Nombre de la empresa (opcional)',
      companySize: 'Tamaño de la empresa',
      sessionsInterest: '¿Qué sesiones te interesan?',
      submit: 'Enviar',
      privacyConsent: 'Acepto la Declaración de privacidad de Bether.',
      contactConsent:
        'Quiero que Bether me contacte y pueda enviarme información comercial.',
      selectOption: 'Seleccione una opción',
    },
  },
  en: {
    titleBefore: 'Where Your Fall Short, ',
    titleUnderline: 'We Step In',
    subtitle: 'Discover Our Services to Develop Your Team',
    categories: [
      {
        title: 'Productivity Boosters',
        color: '#FF2300',
        icon: faListCheck,
        estimatedTime: '4h',
        estimatedDays: 2,
      },
      {
        title: 'Communication Boosters',
        color: '#FF4500',
        icon: faComments,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Team Boosters',
        color: '#FF8C00',
        icon: faPeopleLine,
        estimatedTime: '4h',
        estimatedDays: 2,
      },
      {
        title: 'Innovation Boosters',
        color: '#FFA500',
        icon: faFlaskVial,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Meeting Boosters',
        color: '#FFB300',
        icon: faHandshake,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Strategy Boosters',
        color: '#FFC107',
        icon: faChess,
        estimatedTime: '4h',
        estimatedDays: 1,
      },
      {
        title: 'Personalized Sessions',
        color: '#FF6F00',
        icon: faPeopleArrows,
        estimatedTime: '1h',
        estimatedDays: 1,
      },
    ],
    contactForm: {
      firstName: 'Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      companyName: 'Company Name (optional)',
      companySize: 'Company Size',
      sessionsInterest: 'Which sessions interest you?',
      submit: 'Submit',
      privacyConsent: "I agree to Bether's Privacy Statement.",
      contactConsent:
        'I want Bether to contact me and send me commercial information.',
      selectOption: 'Select an option',
    },
  },
};

function Servicios({ language }) {
  const { titleBefore, titleUnderline, subtitle, categories, contactForm } =
    translations[language];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Estado del formulario (incluye lastName)
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    companySize: '',
    sessions: [],
  });

  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedContact, setAcceptedContact] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Determina cuántas cards mostrar y también si es mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCardsToShow(1);
      } else {
        setCardsToShow(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Usamos la lógica de cardsToShow para definir si es mobile
  const isMobile = cardsToShow === 1;

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? categories.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % categories.length);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData((prevData) => ({ ...prevData, [name]: value }));
    if (name === 'email') {
      setEmailError(!isValidEmail(value));
    }
  };

  const resetCaptchaVerification = () => {
    setCaptchaToken(null);
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
    }
  };

  const handleSessionChange = (sessionTitle) => {
    setContactData((prevData) => {
      const sessions = prevData.sessions;
      if (sessions.includes(sessionTitle)) {
        return {
          ...prevData,
          sessions: sessions.filter((s) => s !== sessionTitle),
        };
      } else {
        return { ...prevData, sessions: [...sessions, sessionTitle] };
      }
    });
  };

  const clearFormularyData = () => {
    setContactData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companySize: '',
      sessions: [],
    });
    setAcceptedPrivacy(false);
    setAcceptedContact(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (
      !contactData.firstName ||
      !contactData.lastName ||
      !contactData.email ||
      !contactData.phone ||
      !contactData.companySize
    ) {
      alert(
        language === 'es'
          ? 'Por favor, completa todos los campos requeridos.'
          : 'Please fill out all required fields.'
      );
      return;
    }

    if (!isValidEmail(contactData.email)) {
      alert(
        language === 'es'
          ? 'Por favor, introduce un email válido.'
          : 'Please provide a valid email.'
      );
      return;
    }

    if (contactData.sessions.length === 0) {
      alert(
        language === 'es'
          ? 'Por favor, selecciona al menos una sesión de interés.'
          : 'Please select at least one session of interest.'
      );
      return;
    }

    if (!acceptedPrivacy || !acceptedContact) {
      alert(
        language === 'es'
          ? 'Por favor, acepta todas las condiciones.'
          : 'Please accept all conditions.'
      );
      return;
    }

    if (!captchaToken) {
      alert(
        language === 'es'
          ? 'Por favor, verifica que eres humano.'
          : 'Please verify that you are human.'
      );
      return;
    }

    const {
      firstName,
      lastName,
      email,
      phone,
      companyName,
      companySize,
      sessions,
    } = contactData;
    const payload = {
      firstName,
      lastName,
      email,
      phone,
      companyName,
      companySize: companySizeValue[companySize],
      sessions: sessions.map((elem) => courseValueMapping[elem]),
      language,
      captchaToken,
    };

    try {
      setLoading(true);
      await mailerAPI.servicesNewsletterSubscribe(payload);
      clearFormularyData();
      setIsModalOpen(false);
    } catch (error) {
      const { status } = error;
      const [messageEs, messageEn] =
        errorOnSubscriptionMessages[status] ||
        errorOnSubscriptionMessages.default;
      alert(language === 'es' ? messageEs : messageEn);
    } finally {
      setLoading(false);
      resetCaptchaVerification();
    }
  };

  const openGeneralModal = () => {
    setContactData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companySize: '',
      sessions: [],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const visibleCards = Array(cardsToShow)
    .fill()
    .map((_, i) => categories[(currentIndex + i) % categories.length]);

  const cardDynamicStyle = {
    minWidth: cardsToShow === 1 ? '90%' : '30%',
  };

  return (
    <section style={styles.section}>
      <h2 style={styles.sectionTitle}>
        {titleBefore}
        <span style={styles.titleUnderline}>{titleUnderline}</span>
      </h2>
      <p style={styles.sectionSubtitle}>{subtitle}</p>
      <div style={styles.carouselContainer}>
        <button onClick={handlePrev} style={styles.arrow}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div style={styles.carousel}>
          {visibleCards.map((category, index) => (
            <div
              key={index}
              style={{
                ...styles.card,
                ...cardDynamicStyle,
                backgroundColor: 'transparent',
                border: `2px solid ${category.color}`,
              }}
            >
              <div style={styles.cardContent}>
                <FontAwesomeIcon
                  icon={category.icon}
                  size="3x"
                  style={{ marginBottom: '10px', color: category.color }}
                />
                <h3 style={styles.cardTitle}>{category.title}</h3>
              </div>
              <div style={styles.cardFooter}>
                <div style={styles.footerItem}>
                  <FontAwesomeIcon
                    icon={faHourglassHalf}
                    style={{ marginRight: '5px', color: category.color }}
                  />
                  <span>{category.estimatedTime}</span>
                </div>
                <div style={styles.footerItem}>
                  <FontAwesomeIcon
                    icon={faCalendar}
                    style={{ marginRight: '5px', color: category.color }}
                  />
                  <span>
                    {category.estimatedDays}
                    {language === 'es' ? ' días' : ' days'}
                  </span>
                </div>
              </div>
              <button
                style={{
                  ...styles.moreInfoButton,
                  backgroundColor: category.color,
                }}
              >
                <FontAwesomeIcon
                  icon={faFileArrowDown}
                  style={{ marginRight: '5px' }}
                />
                Dossier
              </button>
            </div>
          ))}
        </div>
        <button onClick={handleNext} style={styles.arrow}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <button onClick={openGeneralModal} style={styles.generalButton}>
        <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: '8px' }} />
        {language === 'es'
          ? 'Reserva una sesión con nosotros'
          : 'Book a session with us'}
      </button>

      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div
            style={
              isMobile
                ? {
                    ...styles.modalContent,
                    width: '95%',
                    padding: '15px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                  }
                : {
                    ...styles.modalContent,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                  }
            }
          >
            <button onClick={closeModal} style={styles.modalCloseButton}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <h2 style={styles.modalTitle}>
              {language === 'es' ? 'Contáctanos' : 'Contact us'}
            </h2>
            <form onSubmit={handleContactSubmit} style={styles.contactForm}>
              {/* Sección Superior: Dos columnas (o una sola en mobile) */}
              <div
                style={
                  isMobile
                    ? { ...styles.topSection, flexDirection: 'column', gap: '20px' }
                    : styles.topSection
                }
              >
                {/* Columna Izquierda */}
                <div
                  style={
                    isMobile
                      ? { ...styles.topLeftColumn, width: '100%', marginRight: 0 }
                      : styles.topLeftColumn
                  }
                >
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.firstName}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={contactData.firstName}
                      onChange={handleContactChange}
                      style={styles.contactInput}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.lastName}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={contactData.lastName}
                      onChange={handleContactChange}
                      style={styles.contactInput}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.email}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="email"
                        name="email"
                        value={contactData.email}
                        onChange={handleContactChange}
                        style={{
                          ...styles.contactInput,
                          paddingRight: '35px',
                          borderColor: emailError ? 'red' : '#ccc',
                        }}
                        required
                      />
                      {emailError && (
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'red',
                            cursor: 'pointer',
                          }}
                          title={
                            language === 'es'
                              ? 'Correo inválido'
                              : 'Invalid email'
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* Columna Derecha */}
                <div
                  style={
                    isMobile
                      ? { ...styles.topRightColumn, width: '100%' }
                      : styles.topRightColumn
                  }
                >
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactData.phone}
                      onChange={handleContactChange}
                      style={styles.contactInput}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.companyName}
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={contactData.companyName}
                      onChange={handleContactChange}
                      style={styles.contactInput}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.contactLabel}>
                      {contactForm.companySize}
                    </label>
                    <select
                      name="companySize"
                      value={contactData.companySize}
                      onChange={handleContactChange}
                      style={styles.contactInput}
                      required
                    >
                      <option value="" disabled>
                        {contactForm.selectOption}
                      </option>
                      {companySizes[language].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* Línea horizontal */}
              <div style={styles.horizontalDivider}></div>
              {/* Resto del formulario */}
              <div style={styles.restSection}>
                <div style={styles.formGroup}>
                  <p style={styles.contactLabel}>
                    {contactForm.sessionsInterest}
                  </p>
                  <div style={styles.checkboxContainer}>
                    {categories.map((cat, i) => (
                      <div key={i} style={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          id={`session-${i}`}
                          name="sessions"
                          checked={contactData.sessions.includes(cat.title)}
                          onChange={() => handleSessionChange(cat.title)}
                        />
                        <label
                          htmlFor={`session-${i}`}
                          style={{
                            marginLeft: '5px',
                            fontSize: '14px',
                            fontWeight: '400', // Regular
                          }}
                        >
                          {cat.title}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {/* HCaptcha centrado */}
                <div style={styles.hcaptchaContainer}>
                  <HCaptcha
                    sitekey="48ea562e-283e-49d0-87c8-0afeb0f0a860"
                    onVerify={setCaptchaToken}
                    onError={resetCaptchaVerification}
                    ref={captchaRef}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    ...styles.contactButton,
                    ...(loading
                      ? { backgroundColor: '#ccc', cursor: 'not-allowed' }
                      : {}),
                  }}
                  disabled={loading}
                >
                  {loading ? 'Procesando...' : contactForm.submit}
                </button>
                <div style={styles.smallCheckbox}>
                  <input
                    type="checkbox"
                    id="privacyConsentContact"
                    name="privacyConsent"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    required
                  />
                  <label
                    htmlFor="privacyConsentContact"
                    style={{ marginLeft: '5px', fontWeight: '400' }}
                  >
                    {language === 'es' ? (
                      <>
                        Acepto la{' '}
                        <a
                          href="/legal"
                          target="_blank"
                          style={{
                            color: '#FF2300',
                            textDecoration: 'underline',
                          }}
                        >
                          Declaración de privacidad
                        </a>{' '}
                        de Bether.
                      </>
                    ) : (
                      contactForm.privacyConsent
                    )}
                  </label>
                </div>
                <div style={styles.smallCheckbox}>
                  <input
                    type="checkbox"
                    id="contactConsentContact"
                    name="contactConsent"
                    checked={acceptedContact}
                    onChange={(e) => setAcceptedContact(e.target.checked)}
                    required
                  />
                  <label
                    htmlFor="contactConsentContact"
                    style={{ marginLeft: '5px', fontWeight: '400' }}
                  >
                    {contactForm.contactConsent}
                  </label>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

const styles = {
  section: {
    padding: '90px 20px',
    backgroundColor: '#f9f9f9',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: '36px',
    fontWeight: '700', // Bold
    color: '#333',
    marginBottom: '10px',
    marginTop: '50px',
  },
  titleUnderline: {
    color: '#FF2300',
    display: 'inline-block',
  },
  sectionSubtitle: {
    fontSize: '18px',
    fontWeight: '400', // Regular
    color: '#666',
    marginBottom: '40px',
  },
  carouselContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    overflow: 'hidden',
  },
  arrow: {
    fontSize: '30px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: '#333',
    padding: '0 20px',
  },
  carousel: {
    display: 'flex',
    gap: '20px',
    flex: 1,
    justifyContent: 'center',
    transition: 'transform 0.3s ease',
  },
  card: {
    height: '300px',
    borderRadius: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: '#333',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    padding: '20px',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '700', // Bold
    marginTop: '10px',
    textAlign: 'center',
  },
  cardFooter: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
  },
  footerItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: '400', // Regular
  },
  moreInfoButton: {
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '4px 10px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '10px',
    fontWeight: '700', // Bold
  },
  generalButton: {
    marginTop: '40px',
    padding: '12px 24px',
    backgroundColor: '#FF2300',
    color: '#ffffff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '700', // Bold
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '600px',
    position: 'relative',
  },
  modalCloseButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700', // Bold
    marginBottom: '20px',
    textAlign: 'center',
  },
  contactForm: {
    textAlign: 'left',
  },
  topSection: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  topLeftColumn: {
    flex: 1,
    minWidth: '250px',
    marginRight: '20px',
  },
  topRightColumn: {
    flex: 1,
    minWidth: '250px',
  },
  horizontalDivider: {
    height: '1px',
    backgroundColor: '#FF2300',
    width: '100%',
    margin: '20px 0',
  },
  restSection: {},
  formGroup: {
    marginBottom: '15px',
    textAlign: 'left',
  },
  contactLabel: {
    display: 'block',
    marginBottom: '5px',
    color: '#333',
    fontWeight: '400', // Regular
  },
  contactInput: {
    width: '100%',
    padding: '10px 15px',
    height: '46px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400', // Regular
    outline: 'none',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box',
  },
  contactButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#FF2300',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '700', // Bold
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    boxSizing: 'border-box',
    marginTop: '10px',
  },
  smallCheckbox: {
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
    marginLeft: '30px',
    justifyContent: 'flex-start',
  },
  checkboxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '10px',
  },
  checkboxItem: {
    width: '48%',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  },
  hcaptchaContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
};

export default Servicios;
